/* eslint-disable max-lines-per-function */
import { Button, ChangeDot, HeroLayout, Link, Surface, Text } from '@change/ds-core-ui';
import { EditIcon, LocationPinIcon } from '@change/ds-icons';

import { Translate, TranslateHtml, TranslatePluralHtml } from '@change-corgi/core/react/i18n';

import { GooglePlacesProvider } from 'src/app/shared/hooks/googlePlaces';
import { isLoaded } from 'src/app/shared/utils/async';

import { EditLocationModal } from './components/EditLocationModal';
import { useLocalSection } from './hooks/useLocalSection';

export function LocalSection() {
	const {
		data: { locationData, isModalOpen },
		actions,
	} = useLocalSection();

	if (!isLoaded(locationData)) return null;

	return (
		<Surface className="rounded" color="surface-neutral" as="section">
			<HeroLayout
				reverseMd
				reverseSm
				variant="1/2"
				media={
					<div className="flex h-full items-center space-y-4 bg-[url(https://static.change.org/images/map-pattern.svg)] bg-cover bg-center px-[61px] py-10 sm:p-6">
						<div className="space-y-4 md:space-y-2">
							{/* Contains Signatures */}
							<div className="flex items-baseline gap-3">
								<div>
									<ChangeDot size="medium" />
								</div>
								<div>
									<Text
										className="lg:leading-[40px]"
										variant="body"
										size="x-large"
										data-testid="local-section-signature-count-plural"
									>
										<TranslatePluralHtml
											value="corgi.homepage.local_section.stats.signatures"
											count={locationData.value.signaturesCount}
											replacements={{
												signatureCountLocalized: locationData.value.signaturesCountLocalized,
											}}
										/>
									</Text>
								</div>
							</div>
							{/* Contains Petitions */}
							<div className="flex items-baseline gap-3">
								<div>
									<ChangeDot size="medium" />
								</div>
								<div>
									<Text
										className="lg:leading-[40px]"
										variant="body"
										size="x-large"
										data-testid="local-section-petition-count-plural"
									>
										<TranslatePluralHtml
											value="corgi.homepage.local_section.stats.petitions"
											count={locationData.value.petitionsCount}
											replacements={{
												petitionCountLocalized: locationData.value.petitionsCountLocalized,
											}}
										/>
									</Text>
								</div>
							</div>
							{/* Contains Decision Makers */}
							<div className="flex items-baseline gap-3">
								<div>
									<ChangeDot size="medium" />
								</div>
								<div>
									<Text
										className="lg:leading-[40px]"
										variant="body"
										size="x-large"
										data-testid="local-section-decision-makers-count-plural"
									>
										<TranslatePluralHtml
											value="corgi.homepage.local_section.stats.decision_makers"
											count={locationData.value.decisionMakersCount}
											replacements={{
												decisionMakerCountLocalized: locationData.value.decisionMakersCountLocalized,
											}}
										/>
									</Text>
								</div>
							</div>
						</div>
					</div>
				}
			>
				<div className="flex items-center p-8 sm:p-6 md:p-12">
					{/* Outer Wrapper */}
					<div className="space-y-[24px]">
						<div className="space-y-[16px]">
							{/* Contains "Happening in Los Angeles" */}
							<div>
								<div className="inline-block">
									{/* Happening In Location */}
									<Text as="h2" variant="heading" className="m-0">
										<Translate
											key={locationData.value.slug}
											value="corgi.homepage.local_section.title"
											replacements={{ location: locationData.value.shortName }}
										/>
									</Text>
								</div>
							</div>
							{/* Contains "Showing Results for Location" */}
							<div className="flex space-x-1">
								<LocationPinIcon />
								<div className="flex items-center space-x-2">
									{/* Showing results for */}
									<GooglePlacesProvider>
										<EditLocationModal
											trigger={
												<Text>
													<TranslateHtml
														value="corgi.homepage.local_section.subheading"
														replacements={{
															location: (
																<span className="inline-flex items-center">
																	<Link
																		as="button"
																		data-test-id="edit-location-link"
																		data-qa="edit-location-link"
																		onClick={() => actions.handleEditLocationClick(locationData.value)}
																		className="text-content-primary"
																	>
																		{locationData.value.name}
																		<EditIcon className="ml-2" />
																	</Link>
																</span>
															),
														}}
													/>
												</Text>
											}
											isOpen={isModalOpen}
											onClose={actions.handleCloseModal}
										/>
									</GooglePlacesProvider>
								</div>
							</div>
						</div>
						<div>
							{/* Contains "Explore Local Petitions" Button */}
							<div>
								<Button
									as="a"
									href={locationData.value.localPageUrl}
									variant="secondary"
									size="large"
									data-test-id="homepage-v2-local-section-explore-button"
									data-qa="homepage-v2-local-section-explore-button"
									className="bg-transparent"
									onClick={() => actions.onLocalSectionClick(locationData.value)}
								>
									<Translate value="corgi.homepage.local_section.explore_button" />
								</Button>
							</div>
						</div>
					</div>
				</div>
			</HeroLayout>
		</Surface>
	);
}
